import React from 'react';
import { alpha, Box, Card, CardContent, Collapse, makeStyles, Typography } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import HomeIcon from '@material-ui/icons/Home';
import BuildIcon from '@material-ui/icons/Build';
import InvertColorsIcon from '@material-ui/icons/InvertColors';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import { useThresholdData, useTotalANCs } from '../../hooks/useMetabaseStats';
import { CRM_COLORS, CRM_RADIUS } from '../../theme/crmColors';
import { BREAKPOINTS } from '../../theme/breakpoints';

const useStyles = makeStyles(() => ({
    // Common icon container for stats
    modernStatIcon: {
        width: 40,
        height: 40,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    modernStatIconBgPrimary: { backgroundColor: alpha(CRM_COLORS.primary, 0.1) },
    modernStatIconBgSuccess: { backgroundColor: alpha(CRM_COLORS.success, 0.1) },
    modernStatIconBgWarning: { backgroundColor: alpha(CRM_COLORS.warning, 0.1) },
    modernStatIconBgError: { backgroundColor: alpha(CRM_COLORS.error, 0.1) },

    // Cards
    modernStatCard: {
        borderRadius: CRM_RADIUS.card,
        boxShadow: CRM_COLORS.shadow,
        border: `1px solid ${CRM_COLORS.border}`,
        transition: 'box-shadow 0.15s ease',
        '&:hover': {
            boxShadow: CRM_COLORS.shadowHover,
        },
    },
    modernStatCardContent: {
        padding: '16px !important',
    },
    cardContentGrid: {
        display: 'grid',
        gap: 16,
    },

    // Text
    modernStatValue: {
        fontSize: '2rem',
        fontWeight: 700,
        color: CRM_COLORS.text.primary,
        lineHeight: 1,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            fontSize: '1.25rem',
        },
    },
    modernStatLabel: {
        fontSize: '0.875rem',
        color: CRM_COLORS.text.secondary,
        fontWeight: 500,
        lineHeight: 1,
    },

    // Header row (clickable)
    headerRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 24px',
        cursor: 'pointer',
        backgroundColor: CRM_COLORS.surface,
        borderRadius: CRM_RADIUS.card,
        border: `1px solid ${CRM_COLORS.border}`,
        boxShadow: CRM_COLORS.shadow,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            padding: '12px 16px',
            gap: 8,
        },
    },
    headerRowOpen: {
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        borderBottom: 'none',
    },
    headerLeftRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 32,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            gap: 12,
            flexWrap: 'wrap',
        },
    },
    statItemRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            gap: 6,
        },
    },
    topIconBox: {
        width: 32,
        height: 32,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            width: 24,
            height: 24,
        },
    },
    topIconBgPrimary: { backgroundColor: alpha(CRM_COLORS.primary, 0.1) },
    topIconBgWarning: { backgroundColor: alpha(CRM_COLORS.warning, 0.1) },
    topIconBgError: { backgroundColor: alpha(CRM_COLORS.error, 0.1) },

    iconSmall: { fontSize: 18 },
    iconPrimary: { color: CRM_COLORS.primary },
    iconSuccess: { color: CRM_COLORS.success },
    iconWarning: { color: CRM_COLORS.warning },
    iconError: { color: CRM_COLORS.error },

    headerCount: {
        fontWeight: 600,
        color: '#333',
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            fontSize: '1rem',
        },
    },

    expandIcon: {
        transition: 'transform 0.3s ease',
        transform: 'rotate(0deg)',
    },
    expandIconOpen: {
        transform: 'rotate(180deg)',
    },

    // Content
    contentPadding: {
        padding: 16,
        backgroundColor: CRM_COLORS.surface,
        borderRadius: CRM_RADIUS.card,
        border: `1px solid ${CRM_COLORS.border}`,
        boxShadow: CRM_COLORS.shadow,
    },
    contentOpen: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
    },
    statGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(4, auto)',
        gap: 16,
        [`@media (max-width:${BREAKPOINTS.mobile}px)`]: {
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 8,
        },
    },

    // Level rows
    levelsRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: '0 8px',
    },
    levelCol: {
        textAlign: 'center',
        flex: 1,
    },
    levelNumberSuccess: {
        color: CRM_COLORS.success,
        fontSize: '1.1rem',
        fontWeight: 600,
        lineHeight: 1,
    },
    levelNumberWarning: {
        color: CRM_COLORS.warning,
        fontSize: '1.1rem',
        fontWeight: 600,
        lineHeight: 1,
    },
    levelNumberError: {
        color: CRM_COLORS.error,
        fontSize: '1.1rem',
        fontWeight: 600,
        lineHeight: 1,
    },
    levelCaption: {
        color: CRM_COLORS.text.secondary,
        fontSize: '0.65rem',
        lineHeight: 1,
    },
    border: {},
}));

const DataSection: React.FC = () => {
    const classes = useStyles();
    const [dataCollapseOpen, setDataCollapseOpen] = React.useState<boolean>(false);
    const [isEntering, setIsEntering] = React.useState(false);
    const [isExiting, setIsExiting] = React.useState(false);

    // Maintain a clean visual seam while open or during transitions
    const keepSeam = dataCollapseOpen || isEntering || isExiting;
    const { totalANCs, miseEnService, miseEnServiceGPS, preteALivrer, preteAPoser, travauxPoseEffectue, travauxPoseEffectueGPS, loading: metabaseLoading } = useTotalANCs();
    const { data: thresholdData, loading: thresholdLoading } = useThresholdData();

    const maintenanceLevels = React.useMemo(() => {
        if (!thresholdData?.maintenance) {
            return { level1: 0, level2: 0, level3: 0 };
        }
        return {
            level1: thresholdData.maintenance['threshold.level.1'] || 0,
            level2: thresholdData.maintenance['threshold.level.2'] || 0,
            level3: thresholdData.maintenance['threshold.level.3'] || 0,
        };
    }, [thresholdData]);

    const vidangeLevels = React.useMemo(() => {
        if (!thresholdData?.vidange) {
            return { level1: 0, level2: 0, level3: 0 };
        }
        return {
            level1: thresholdData.vidange['threshold.level.1'] || 0,
            level2: thresholdData.vidange['threshold.level.2'] || 0,
            level3: thresholdData.vidange['threshold.level.3'] || 0,
        };
    }, [thresholdData]);

    const maintenanceUrgent = (maintenanceLevels.level2 || 0) + (maintenanceLevels.level3 || 0);
    const vidangeUrgent = (vidangeLevels.level2 || 0) + (vidangeLevels.level3 || 0);

    return (
        <Box>
            <Box
                onClick={() => setDataCollapseOpen(!dataCollapseOpen)}
                className={`${classes.headerRow} ${keepSeam ? classes.headerRowOpen : ''}`}
            >
                <Box className={classes.headerLeftRow}>
                    {/* Total ANCs */}
                    <Box className={classes.statItemRow}>
                        <Box className={`${classes.topIconBox} ${classes.topIconBgPrimary}`}>
                            <HomeIcon className={`${classes.iconSmall} ${classes.iconPrimary}`} />
                        </Box>
                        <Typography variant='h6' className={classes.headerCount}>
                            {metabaseLoading || thresholdLoading ? '...' : (totalANCs || 0).toLocaleString()}
                        </Typography>
                    </Box>

                    {/* Maintenance Urgent (L2+L3) */}
                    <Box className={classes.statItemRow}>
                        <Box className={`${classes.topIconBox} ${classes.topIconBgWarning}`}>
                            <BuildIcon className={`${classes.iconSmall} ${classes.iconWarning}`} />
                        </Box>
                        <Typography variant='h6' className={classes.headerCount}>
                            {metabaseLoading || thresholdLoading ? '...' : maintenanceUrgent}
                        </Typography>
                    </Box>

                    {/* Vidange Urgent (L2+L3) */}
                    <Box className={classes.statItemRow}>
                        <Box className={`${classes.topIconBox} ${classes.topIconBgError}`}>
                            <InvertColorsIcon className={`${classes.iconSmall} ${classes.iconError}`} />
                        </Box>
                        <Typography variant='h6' className={classes.headerCount}>
                            {metabaseLoading || thresholdLoading ? '...' : vidangeUrgent}
                        </Typography>
                    </Box>
                </Box>

                <ExpandMoreIcon className={`${classes.expandIcon} ${dataCollapseOpen ? classes.expandIconOpen : ''}`} />
            </Box>

            <Collapse
                in={dataCollapseOpen}
                timeout={300}
                onEnter={() => setIsEntering(true)}
                onEntered={() => setIsEntering(false)}
                onExit={() => setIsExiting(true)}
                onExited={() => setIsExiting(false)}
                unmountOnExit
            >
                <Box className={`${classes.contentPadding} ${keepSeam ? classes.contentOpen : ''}`}>
                    <Box className={classes.statGrid}>
                        {[
                            {
                                icon: (
                                    <Box className={`${classes.modernStatIcon} ${classes.modernStatIconBgPrimary}`}>
                                        <HomeIcon className={classes.iconPrimary} />
                                    </Box>
                                ),
                                number: totalANCs || 0,
                                label: 'Total ANCs',
                            },
                            {
                                icon: (
                                    <Box className={`${classes.modernStatIcon} ${classes.modernStatIconBgSuccess}`}>
                                        <LocalShippingIcon className={classes.iconSuccess} />
                                    </Box>
                                ),
                                number: preteALivrer || 0,
                                label: 'Prête à livrer',
                            },
                            {
                                icon: (
                                    <Box className={`${classes.modernStatIcon} ${classes.modernStatIconBgSuccess}`}>
                                        <BuildIcon className={classes.iconSuccess} />
                                    </Box>
                                ),
                                number: preteAPoser || 0,
                                label: 'Prête à poser',
                            },
                            {
                                icon: (
                                    <Box className={`${classes.modernStatIcon} ${classes.modernStatIconBgSuccess}`}>
                                        <BuildIcon className={classes.iconSuccess} />
                                    </Box>
                                ),
                                number: (() => {
                                    const mainCount = travauxPoseEffectue || 0;
                                    const gpsCount = travauxPoseEffectueGPS || 0;
                                    return (
                                        <span>
                                            {mainCount} <span style={{ fontSize: '0.7em', opacity: 0.8 }}>({gpsCount} GPS)</span>
                                        </span>
                                    );
                                })(),
                                label: 'Traveaux de pose effectué',
                            },
                            {
                                icon: (
                                    <Box className={`${classes.modernStatIcon} ${classes.modernStatIconBgSuccess}`}>
                                        <PlayArrowIcon className={classes.iconSuccess} />
                                    </Box>
                                ),
                                number: (() => {
                                    const mainCount = miseEnService || 0;
                                    const gpsCount = miseEnServiceGPS || 0;
                                    return (
                                        <span>
                                            {mainCount} <span style={{ fontSize: '0.7em', opacity: 0.8 }}>({gpsCount} GPS)</span>
                                        </span>
                                    );
                                })(),
                                label: 'En service',
                            },
                            {
                                icon: (
                                    <Box className={`${classes.modernStatIcon} ${classes.modernStatIconBgWarning}`}>
                                        <BuildIcon className={classes.iconWarning} />
                                    </Box>
                                ),
                                number:
                                    metabaseLoading || thresholdLoading ? (
                                        '...'
                                    ) : (
                                        <Box className={classes.levelsRow}>
                                            <Box className={classes.levelCol}>
                                                <Typography variant='h6' className={classes.levelNumberSuccess}>
                                                    {maintenanceLevels.level1}
                                                </Typography>
                                                <Typography variant='caption' className={classes.levelCaption}>
                                                    L1
                                                </Typography>
                                            </Box>
                                            <Box style={{ textAlign: 'center', flex: 1 }}>
                                                <Typography
                                                    variant='h6'
                                                    style={{
                                                        color: CRM_COLORS.warning,
                                                        fontSize: '1.1rem',
                                                        fontWeight: 600,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {maintenanceLevels.level2}
                                                </Typography>
                                                <Typography
                                                    variant='caption'
                                                    style={{
                                                        color: CRM_COLORS.text.secondary,
                                                        fontSize: '0.65rem',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    L2
                                                </Typography>
                                            </Box>
                                            <Box style={{ textAlign: 'center', flex: 1 }}>
                                                <Typography
                                                    variant='h6'
                                                    style={{
                                                        color: CRM_COLORS.error,
                                                        fontSize: '1.1rem',
                                                        fontWeight: 600,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {maintenanceLevels.level3}
                                                </Typography>
                                                <Typography
                                                    variant='caption'
                                                    style={{
                                                        color: CRM_COLORS.text.secondary,
                                                        fontSize: '0.65rem',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    L3
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ),
                                label: 'Maintenance',
                            },
                            {
                                icon: (
                                    <Box className={`${classes.modernStatIcon} ${classes.modernStatIconBgError}`}>
                                        <InvertColorsIcon className={classes.iconError} />
                                    </Box>
                                ),
                                number:
                                    metabaseLoading || thresholdLoading ? (
                                        '...'
                                    ) : (
                                        <Box
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                width: '100%',
                                                padding: '0 8px',
                                            }}
                                        >
                                            <Box style={{ textAlign: 'center', flex: 1 }}>
                                                <Typography
                                                    variant='h6'
                                                    style={{
                                                        color: CRM_COLORS.success,
                                                        fontSize: '1.1rem',
                                                        fontWeight: 600,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {vidangeLevels.level1}
                                                </Typography>
                                                <Typography
                                                    variant='caption'
                                                    style={{
                                                        color: CRM_COLORS.text.secondary,
                                                        fontSize: '0.65rem',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    L1
                                                </Typography>
                                            </Box>
                                            <Box style={{ textAlign: 'center', flex: 1 }}>
                                                <Typography
                                                    variant='h6'
                                                    style={{
                                                        color: CRM_COLORS.warning,
                                                        fontSize: '1.1rem',
                                                        fontWeight: 600,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {vidangeLevels.level2}
                                                </Typography>
                                                <Typography
                                                    variant='caption'
                                                    style={{
                                                        color: CRM_COLORS.text.secondary,
                                                        fontSize: '0.65rem',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    L2
                                                </Typography>
                                            </Box>
                                            <Box style={{ textAlign: 'center', flex: 1 }}>
                                                <Typography
                                                    variant='h6'
                                                    style={{
                                                        color: CRM_COLORS.error,
                                                        fontSize: '1.1rem',
                                                        fontWeight: 600,
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    {vidangeLevels.level3}
                                                </Typography>
                                                <Typography
                                                    variant='caption'
                                                    style={{
                                                        color: CRM_COLORS.text.secondary,
                                                        fontSize: '0.65rem',
                                                        lineHeight: 1,
                                                    }}
                                                >
                                                    L3
                                                </Typography>
                                            </Box>
                                        </Box>
                                    ),
                                label: 'Vidange',
                            },
                        ].map(({ icon, number, label }) => (
                            <Card className={classes.modernStatCard} key={label}>
                                <CardContent
                                    className={classes.modernStatCardContent}
                                    style={{
                                        display: 'grid',
                                        gap: '16px',
                                    }}
                                >
                                    {icon}

                                    <Typography
                                        className={classes.modernStatValue}
                                        style={{
                                            lineHeight: 1,
                                        }}
                                    >
                                        {(label === 'Total ANCs' ||
                                            label === 'En service' ||
                                            label === 'Prête à livrer' ||
                                            label === 'Prête à poser' ||
                                            label === 'Traveaux de pose effectué') &&
                                        (metabaseLoading || thresholdLoading)
                                            ? '...'
                                            : number}
                                    </Typography>

                                    <Typography
                                        className={classes.modernStatLabel}
                                        style={{
                                            lineHeight: 1,
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                </CardContent>
                            </Card>
                        ))}
                    </Box>
                </Box>
            </Collapse>
        </Box>
    );
};

export default DataSection;
